import { Door, DoorBatteryWarningLevel, Location } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { useMemo } from 'react';
import { getLockGroupsThatLockBelongTo } from 'utils/Lock/Lock';
import { useSearch } from './useSearch';

const searchKeys = ['name', 'groups', 'hardwareIdentifier', 'batteryStatus'];

const getSearchableGroupNames = (groups: Location[], translation: Translation): string[] => {
  const names = groups.map((group) => group.name).filter((name): name is string => Boolean(name));
  return names.length > 0 ? names : [translation.no_groups];
};

const getBatteryStatus = (device: Door, translation: Translation): string => {
  switch (device.batteryWarningLevel) {
    case DoorBatteryWarningLevel.BatteryLow:
      return translation.low;
    case DoorBatteryWarningLevel.BatteryCritical:
      return translation.critical;
    case DoorBatteryWarningLevel.BatteryEmpty:
      return translation.empty;
    case DoorBatteryWarningLevel.Powered:
      return translation.sync_required;
    case DoorBatteryWarningLevel.BatteryUnknown:
      return translation.unknown;
    case DoorBatteryWarningLevel.BatteryDefault:
    default:
      return '';
  }
};

export function useDeviceSearch({
  devices,
  lockGroups,
  search,
  translation
}: {
  devices: Door[];
  lockGroups: Location[];
  search: string;
  translation: Translation;
}): Door[] {
  const searchableDevices = useMemo(() => {
    return devices.map((device) => {
      const groups = getLockGroupsThatLockBelongTo(lockGroups, device.id);
      return {
        ...device,
        groups: getSearchableGroupNames(groups, translation),
        batteryStatus: getBatteryStatus(device, translation)
      };
    });
  }, [devices, lockGroups, translation]);

  const matchedSearchable = useSearch({ data: searchableDevices, search, keys: searchKeys });

  return useMemo(() => matchedSearchable.map(({ groups, batteryStatus, ...device }) => device), [matchedSearchable]);
}
